import type { CustomAxiosRequestConfig } from '@sa/axios';
import { BACKEND_ERROR_CODE, createFlatRequest, createRequest } from '@sa/axios';
import { useAuthStore } from '@/store/modules/auth';
import { localStg } from '@/utils/storage';
import { getServiceBaseURL } from '@/utils/service';
import { $t } from '@/locales';
import { showErrorMsg } from './shared';
import type { RequestInstanceState } from './type';

const isHttpProxy = import.meta.env.DEV && import.meta.env.VITE_HTTP_PROXY === 'Y';
const { baseURL, otherBaseURL } = getServiceBaseURL(import.meta.env, isHttpProxy);

export const request = createFlatRequest<App.Service.Response, RequestInstanceState>(
  {
    baseURL,
    timeout: 300000,
    headers: {
      // apifoxToken: 'XL299LiMEDZ0H5h3A29PxwQXdMJqWyY2'
    }
  },
  {
    async onRequest(config) {
      const { headers } = config;

      // set token
      const token = localStg.get('token');
      const Authorization = token || null;
      Object.assign(headers, { RememberMe: Authorization });

      return config;
    },
    isBackendSuccess(response) {
      // when the backend response code is "0000"(default), it means the request is success
      // to change this logic by yourself, you can modify the `VITE_SERVICE_SUCCESS_CODE` in `.env` file
      return String(response.data.code) === import.meta.env.VITE_SERVICE_SUCCESS_CODE;
    },
    async onBackendFail(response) {
      const authStore = useAuthStore();

      function handleLogout() {
        authStore.resetStore();
      }

      function logoutAndCleanup() {
        handleLogout();
        window.removeEventListener('beforeunload', handleLogout);

        request.state.errMsgStack = request.state.errMsgStack.filter(msg => msg !== response.data.msg);
      }

      // when the backend response code is in `logoutCodes`, it means the user will be logged out and redirected to login page
      const logoutCodes = import.meta.env.VITE_SERVICE_LOGOUT_CODES?.split(',') || [];
      if (logoutCodes.includes(response.data.code)) {
        handleLogout();
        return null;
      }

      // when the backend response code is in `modalLogoutCodes`, it means the user will be logged out by displaying a modal
      const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
      if (modalLogoutCodes.includes(response.data.code)) {
        request.state.errMsgStack = [...(request.state.errMsgStack || []), response.data.msg];
        // prevent the user from refreshing the page
        window.addEventListener('beforeunload', handleLogout);

        window.$dialog?.error({
          title: 'Error',
          content: response.data.msg,
          positiveText: $t('common.confirm'),
          maskClosable: false,
          closeOnEsc: false,
          onPositiveClick() {
            logoutAndCleanup();
          },
          onClose() {
            logoutAndCleanup();
          }
        });

        return null;
      }

      // when the backend response code is in `expiredTokenCodes`, it means the token is expired, and refresh token
      // the api `refreshToken` can not return error code in `expiredTokenCodes`, otherwise it will be a dead loop, should return `logoutCodes` or `modalLogoutCodes`
      // const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
      // if (expiredTokenCodes.includes(response.data.code) && !request.state.isRefreshingToken) {
      //   request.state.isRefreshingToken = true;

      //   const refreshConfig = await handleRefreshToken(response.config);

      //   request.state.isRefreshingToken = false;

      //   if (refreshConfig) {
      //     return instance.request(refreshConfig) as Promise<AxiosResponse>;
      //   }
      // }

      return null;
    },
    transformBackendResponse(response) {
      return response.data.data;
    },
    onError(error) {
      // when the request is fail, you can show error message

      let message = error.message;
      let backendErrorCode = '';

      // get backend error message and code
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.msg || message;
        backendErrorCode = error.response?.data?.code || '';
      }

      // the error message is displayed in the modal
      const modalLogoutCodes = import.meta.env.VITE_SERVICE_MODAL_LOGOUT_CODES?.split(',') || [];
      if (modalLogoutCodes.includes(backendErrorCode)) {
        return;
      }

      // when the token is expired, refresh token and retry request, so no need to show error message
      const expiredTokenCodes = import.meta.env.VITE_SERVICE_EXPIRED_TOKEN_CODES?.split(',') || [];
      if (expiredTokenCodes.includes(backendErrorCode)) {
        return;
      }

      showErrorMsg(request.state, message);
    }
  }
);
export const get = <T>(url: string, params?: Record<string, any>, config?: CustomAxiosRequestConfig) => {
  return request<T>({
    url,
    method: 'get',
    params,
    ...(config || {})
  });
};

export const post = <T>(url: string, data?: Record<string, any>, config?: CustomAxiosRequestConfig) => {
  return request<T>({
    url,
    method: 'post',
    data,
    ...(config || {})
  });
};

export const download = async (url: string, params?: Record<string, any>, fileName?: string) => {
  const { data, headers } = await request({
    url,
    method: 'post',
    data: params,
    responseType: 'blob'
  });
  if (data) {
    const type = data.type;
    if (type.includes('application/json')) {
      const resData = await new Promise(resolve => {
        const reader = new FileReader();
        reader.onload = e => {
          if (e.target?.readyState === 2) {
            const json = JSON.parse(e.target?.result as string);
            if (json.msg) {
              window.$message?.error(json.msg);
            }
            resolve(json);
          } else {
            resolve(e);
          }
        };
        reader.readAsText(data);
      });
      return { data: resData, headers };
    }
    const disposition = headers?.['content-disposition'];
    // eslint-disable-next-line no-underscore-dangle
    let _fileName = fileName;
    if (disposition && (!_fileName || _fileName === '')) {
      const respcds = disposition.split(';');
      for (let i = 0; i < respcds.length; i += 1) {
        const header = respcds[i];
        // eslint-disable-next-line max-depth
        if (header !== null && header !== '') {
          let headerValue = [];
          // eslint-disable-next-line max-depth
          if (header.includes("*=utf-8''")) {
            headerValue = header.split("*=utf-8''");
          } else {
            headerValue = header.split('=');
          }
          // eslint-disable-next-line max-depth
          if (headerValue !== null && headerValue.length > 0) {
            // eslint-disable-next-line max-depth
            if (headerValue[0].trim().toLowerCase() === 'filename') {
              _fileName = decodeURI(headerValue[1]);
              break;
            }
          }
        }
      }
    }
    if (!_fileName) {
      _fileName = '下载文件.zip';
    }
    // 处理引号
    if (
      (_fileName.startsWith("'") || _fileName.startsWith('"')) &&
      (_fileName.endsWith("'") || _fileName.endsWith('"'))
    ) {
      _fileName = _fileName.substring(1, _fileName.length - 1);
    }

    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(data);
    link.download = _fileName;
    link.click();
    window.URL.revokeObjectURL(link.href);
    window.$message?.success('导出成功');
  }
  return { data, headers };
};

export const upload = async (
  url: string,
  file: File,
  data?: Record<string, any>,
  config?: CustomAxiosRequestConfig
  // eslint-disable-next-line max-params
) => {
  const formData = new FormData();
  if (data) {
    Object.keys(data).forEach(key => {
      formData.append(key, data[key]);
    });
  }
  formData.append('file', file as File);
  return request({
    url,
    method: 'post',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    ...(config || {})
  });
};

export const demoRequest = createRequest<App.Service.DemoResponse>(
  {
    baseURL: otherBaseURL.demo
  },
  {
    async onRequest(config) {
      const { headers } = config;

      // set token
      const token = localStg.get('token');
      const Authorization = token || null;
      Object.assign(headers, { RememberMe: Authorization });

      return config;
    },
    isBackendSuccess(response) {
      // when the backend response code is "200", it means the request is success
      // you can change this logic by yourself
      return response.data.status === '200';
    },
    async onBackendFail(_response) {
      // when the backend response code is not "200", it means the request is fail
      // for example: the token is expired, refresh token and retry request
    },
    transformBackendResponse(response) {
      return response.data.result;
    },
    onError(error) {
      // when the request is fail, you can show error message

      let message = error.message;

      // show backend error message
      if (error.code === BACKEND_ERROR_CODE) {
        message = error.response?.data?.message || message;
      }

      window.$message?.error(message);
    }
  }
);
